import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { ReactFormGenerator, ElementStore } from '../FormBuilderLibrary';
import {
	setCopy,
	setFormSchema,
	setFormHasChanged,
	setFormMetaInfo,
	setFormReadOnly,
	setNewForm
} from '../FormBuilderLibrary/slice';
import {
	useCreateFormMutation,
	usePostFormSchemaMutation,
	useUpdateFormMutation,
	useTemplatizeFormMutation
} from '../../services/endpoints/formBuilderEndpoints';
import {
	checkForEmptyForms,
	checkMissingGroupings,
	capitalizeFirstLetter,
	calculateTotalPoints,
	validateMinAndMaxDate,
	validateMinAndMaxValues,
	parseFieldProperty,
	getTemplateLinkAndText
} from './utils';
import { getLocalAuth } from '../../utils/environmentUtils';
import { isEmptyRTE } from '../../utils/utilFunctions';
import useBuilderPreviewButton from '../../hooks/useBuilderPreviewButton';
import {
	Box,
	Modal,
	IconButton,
	Button,
	Alert,
	Collapse,
	AlertTitle,
	Menu,
	MenuItem
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
	FORM_EDIT_PUBLISHED_WARNING_MESSAGE,
	FORM_EDIT_READY_TO_PUBLISH_WARNING_MESSAGE,
	FORM_EDIT_PUBLISHED_WARNING_TITLE,
	FORM_EDIT_READY_TO_PUBLISH_WARNING_TITLE,
	FORM_EDIT_WARNING_LIST,
	FORM_EDIT_WARNING_CONFIRMATION_MESSAGE,
	SETUP_FORM_SAVE_ERR_MSG,
	TEMP_FORM_SAVE_ERR_MSG
} from '../../constants/appProcessEditWarningMessage';
import CopyFormModal from '../CopyFormModal/CopyFormModal';
import WarningModal from '../WarningModal/WarningModal';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import PasteIcon from '../../assets/customIcons/pasteIcon';
import { MoreHoriz } from '@mui/icons-material';

import LastEdited from '../LastEdited/LastEdited';

const FormBuilderToolbar = ({
	formID,
	data,
	programStatus,
	hasEditPermissions,
	isReadyToPublish,
	isTemplate,
	totalForms,
	setMetaInfoChanged,
	forceRefresh,
	variables,
	hidePreviewButton
}) => {
	const dispatch = useDispatch();
	const _ = require('lodash');
	const [searchParams, setSearchParams] = useSearchParams();

	const [postForm, { isLoading }] = usePostFormSchemaMutation();
	const [createForm] = useCreateFormMutation();
	const [updateForm] = useUpdateFormMutation();
	const [templatizeForm, { isLoading: isTemplatizing }] = useTemplatizeFormMutation();
	const { button: PreviewButton } = useBuilderPreviewButton();

	const accountSource = useSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const reduxFormCopy = useSelector((state) => state?.form?.copy);
	const entireFormSchema = useSelector((state) => state.form.formSchema);
	const formMetaInfo = useSelector((state) => state.form.formMetaInfo);
	const formSchema = useSelector((state) => state.form.formSchema[formID]);
	const isReadOnly = useSelector((state) => state.form.isReadOnly);
	const [isOpen, setIsOpen] = useState(false);
	const [alertBalloon, setAlertBalloon] = useState(false);
	const [errorList, setErrorList] = useState([]);
	const [dataState, setDataState] = useState(data);
	const [previewVisible, setPreviewVisible] = useState();
	const [shortPreviewVisible, setShortPreviewVisible] = useState();
	const [roPreviewVisible, setRoPreviewVisible] = useState();
	const [warningModal, setWarningModal] = useState(false);
	const [templateData, setTemplateData] = useState({});
	const [newFormId, setNewFormId] = useState('');
	const [isCopyFormMenuOpen, setIsCopyFormMenuOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const formId = searchParams.get('formId');

	const warningModalTitle =
		programStatus === 'Published'
			? FORM_EDIT_PUBLISHED_WARNING_TITLE
			: FORM_EDIT_READY_TO_PUBLISH_WARNING_TITLE;
	const warningModalContent =
		programStatus === 'Published'
			? FORM_EDIT_PUBLISHED_WARNING_MESSAGE
			: FORM_EDIT_READY_TO_PUBLISH_WARNING_MESSAGE;
	const {
		link: templateLink,
		linkText: templateLinkText,
		formType
	} = getTemplateLinkAndText(templateData);
	ElementStore.subscribe(() => setDataState(dataState));

	useEffect(() => {
		setDataState(data);
	}, [data, dataState]);

	const created = searchParams.get('created') || false;

	useEffect(() => {
		if (created) {
			const timeoutId = setTimeout(() => {
				searchParams.delete('created');
				setSearchParams(new URLSearchParams(searchParams));
			}, 3000);

			// Cleanup the timeout to avoid potential memory leaks
			return () => clearTimeout(timeoutId);
		}
	}, [created]);

	const closePreview = () => {
		setPreviewVisible(false);
		setShortPreviewVisible(false);
		setRoPreviewVisible(false);
	};

	const handleAlert = (message, status = 'error', errorList = []) => {
		setAlertBalloon({ isOpen: true, message, status });
		setErrorList(errorList);

		if (errorList.length === 0) {
			// I don't know if this is a good idea but until we can reflect in the form
			// user needs time to react to errors
			setTimeout(() => {
				setAlertBalloon((prevAlertBalloon) => ({
					...prevAlertBalloon,
					isOpen: false
				}));
			}, 3000);
		}
	};

	const handleModalClose = () => {
		setIsOpen(false);
	};

	const handleCopyFormMenuClick = (event) => {
		setIsCopyFormMenuOpen(!isCopyFormMenuOpen);
		setAnchorEl(event.currentTarget);
	};

	const handleCopyFormMenuClose = () => {
		setIsCopyFormMenuOpen(false);
		setAnchorEl(null);
	};

	const getFinalForm = () => {
		const noNullForms = entireFormSchema.filter((e) => e !== null && e !== undefined).pop();

		return {
			formLength: noNullForms.length ?? 0,
			formSchema: noNullForms
		};
	};

	const postFormSchema = async (formId) => {
		const { formLength, formSchema } = getFinalForm();

		let options = {
			id: formId,
			payload: formSchema,
			apiToken: apiToken
		};

		if (formLength > 0) {
			await postForm(options).unwrap();
		}
	};

	const getErrorMessages = (fieldErrors, formFields) => {
		// fieldErrors is an array of objects returned from validation
		// each fieldError has properties that coincide with a field property
		// each property has an array of strings that explain the validation issue
		return fieldErrors
			.map((fieldError, index) => {
				if (Object.keys(fieldError).length === 0) return fieldError;
				const fieldLabel = formFields?.[index]?.label;
				return Object.keys(fieldError).map((fieldProperty) => {
					return fieldError[fieldProperty].map((validationMessage) => {
						return `${fieldLabel}'s ${parseFieldProperty(fieldProperty)}: ${validationMessage}`;
					});
				});
			})
			.filter((error) => Object.keys(error).length > 0)
			.flat(Infinity);
	};

	const handleEdit = () => {
		if (isReadyToPublish) {
			setWarningModal(true);
		} else {
			dispatch(setFormReadOnly(false));
		}
	};

	const handleConfirmWarning = () => {
		dispatch(setFormReadOnly(false));
		setWarningModal(false);
	};

	const handleCloseWarningModal = () => {
		setWarningModal(false);
	};

	const handleSave = async () => {
		if (
			entireFormSchema &&
			entireFormSchema.length > 0 &&
			(typeof entireFormSchema[0] === 'undefined' || entireFormSchema[0].length === 0)
		) {
			handleAlert(isTemplate ? TEMP_FORM_SAVE_ERR_MSG : SETUP_FORM_SAVE_ERR_MSG);
			return;
		}
		// Check for undefined and empty string or just spaces on label aka Form Name
		if (
			formMetaInfo &&
			(isEmptyRTE(formMetaInfo.instructions) ||
				!formMetaInfo?.label ||
				formMetaInfo.label.trim() === '')
		) {
			handleAlert(isTemplate ? TEMP_FORM_SAVE_ERR_MSG : SETUP_FORM_SAVE_ERR_MSG);
			return;
		}
		const validateContentLength = (entireFormSchema) => {
			//This errors if the formSchema is empty
			if (!entireFormSchema || entireFormSchema.length < 1 || !entireFormSchema[0]) {
				return null;
			}
			return entireFormSchema[0].some((item) => item.content && item.content.length > 8008);
		};
		const noNullForms = entireFormSchema.filter((e) => e !== null && e !== undefined);
		const hasExceedContentLength = validateContentLength(entireFormSchema);
		const formId = searchParams.get('formId');
		const totalPoints = calculateTotalPoints(noNullForms[0]);
		let createFormOptions = {
			payload: {
				...formMetaInfo,
				order: totalForms + 1,
				totalPoints: searchParams.get('formBuilder') === 'review' ? totalPoints : null,
				form_type: searchParams.get('formBuilder') === 'application' ? 'application' : 'review'
			},

			apiToken: apiToken
		};

		let updateFormOptions = {
			payload: {
				...formMetaInfo,
				stage: formMetaInfo?.stage?.stage_id,
				program: formMetaInfo?.program?.id && {
					programId: formMetaInfo?.program?.id
				},
				totalPoints: searchParams.get('formBuilder') === 'review' ? totalPoints : null,
				form_type: searchParams.get('formBuilder') === 'application' ? 'application' : 'review'
			},

			apiToken: apiToken,
			id: !formId || formId === 'undefined' ? newFormId : formId
		};

		const groupingValidation = checkMissingGroupings(noNullForms);
		if (!groupingValidation.isValid) {
			handleAlert(groupingValidation.errorMessage);
			return;
		} else if (noNullForms?.length === 0) {
			handleAlert(isTemplate ? TEMP_FORM_SAVE_ERR_MSG : SETUP_FORM_SAVE_ERR_MSG);
			return;
		} else if (hasExceedContentLength) {
			handleAlert('Content field cannot exceed 8000 characters.');
			return;
		}

		if (isTemplate) {
			createFormOptions.payload.program = { programId: -1 };
			createFormOptions.payload.is_template = true;

			updateFormOptions.payload.is_template = true;
		}
		if (searchParams.get('isAdHoc')) {
			createFormOptions.payload.is_adhoc = true;
		}

		if ((!formId || formId === 'undefined') && newFormId === '') {
			formMetaInfo &&
				createForm(createFormOptions)
					.unwrap()
					.then(async (res) => {
						searchParams.set('formId', res?.form_id);
						setNewFormId(res?.form_id);
						dispatch(setFormMetaInfo(res));
						dispatch(setNewForm(res?.form_id));

						postFormSchema(res?.form_id)
							.then(() => {
								dispatch(setFormHasChanged(false));
								dispatch(setFormReadOnly(true));

								setMetaInfoChanged && setMetaInfoChanged(false);

								searchParams.set('created', true);
								setSearchParams(searchParams);
							})
							.catch((err) => {
								if (err.status === 400 && !!err.data) {
									const { formSchema } = getFinalForm();
									const errorList = getErrorMessages(err.data, formSchema);
									handleAlert('Form Created With Field Issues', 'error', errorList);
								} else {
									handleAlert(`Form Created With Field Issues: ${err.data}`, 'error');
								}
							});
					})
					.catch(() => '');
		} else {
			updateForm(updateFormOptions)
				.unwrap()
				.then(async (res) => {
					searchParams.set('formId', res?.form_id);
					postFormSchema(res?.form_id)
						.then(() => {
							dispatch(setFormHasChanged(false));
							dispatch(setFormReadOnly(true));
							setMetaInfoChanged && setMetaInfoChanged(false);
							setSearchParams(searchParams);
							handleAlert('Form was successfully updated', 'success');
						})
						.catch((err) => {
							const errorStatus = typeof err.status === 'string' ? err.originalStatus : err.status;
							if (errorStatus >= 400 && errorStatus < 500 && !!err.data) {
								handleAlert(err.data.detail, 'error');
							} else if (errorStatus >= 500 && errorStatus < 600) {
								handleAlert('Server Error', 'error');
							} else {
								handleAlert(`Form Updated With Field Issues: ${err.data}`, 'error');
							}
						});
				})
				.catch(() => '');
		}
	};

	const handleTemplatizeForm = () => {
		let templatizeFormOptions = {
			payload: {
				form_id: formId
			},
			apiToken: apiToken
		};

		if (formId) {
			templatizeForm(templatizeFormOptions)
				.unwrap()
				.then((res) => {
					setTemplateData(res);
					setIsOpen(true);
				})
				.catch((err) => {
					console.log(err);
					handleAlert('An error occurred while trying to templatize the form.');
				})
				.finally(() => {
					setIsCopyFormMenuOpen(false);
				});
		}
	};

	const cloneCopiedElement = (element) => {
		let clonedCopy = _.cloneDeep(element);
		delete clonedCopy.parent_id;
		delete clonedCopy.parent_index;
		delete clonedCopy.component;
		ElementStore.dispatch('create', clonedCopy);
		return clonedCopy;
	};

	const handlePasteClick = () => {
		let response = formSchema ? Object.values(formSchema) : [];
		let clonedResponse = _.cloneDeep(response);
		const finalElements = Array.isArray(reduxFormCopy[0])
			? reduxFormCopy[0]?.map((e) => cloneCopiedElement(e))
			: [cloneCopiedElement(reduxFormCopy[0])];
		dispatch(setFormSchema([formID, [...clonedResponse, ...finalElements]]));
		dispatch(setCopy(null));
		forceRefresh();
	};

	const handleSaveClick = () => {
		const noNullForms = entireFormSchema.filter((e) => e !== null);
		let hasEmptyForms = checkForEmptyForms(noNullForms);
		const groupingValidation = checkMissingGroupings(noNullForms);
		const hasInvalidDate = validateMinAndMaxDate(noNullForms);
		const hasEmptyMinOrMax = validateMinAndMaxValues(
			noNullForms,
			searchParams.get('formBuilder') === 'review'
		);
		const noLabel = !formMetaInfo?.label || formMetaInfo?.label === '';
		const noDescription = searchParams.get('stageId')
			? false
			: !formMetaInfo?.description || formMetaInfo?.description === '';
		const noInstructions = !formMetaInfo?.instructions || formMetaInfo?.instructions === '';
		if (noLabel || noDescription || noInstructions) {
			const form = document.getElementsByClassName('meta-info-form')[0];

			form.click();
		}
		!hasEmptyForms && groupingValidation.isValid && !hasInvalidDate && !hasEmptyMinOrMax
			? handleSave()
			: !groupingValidation.isValid
			? handleAlert(groupingValidation.errorMessage)
			: hasInvalidDate
			? handleAlert('Max date must be later than Min date.')
			: hasEmptyMinOrMax
			? handleAlert('A Min and a Max value must be entered in your Number Input')
			: handleAlert(isTemplate ? TEMP_FORM_SAVE_ERR_MSG : SETUP_FORM_SAVE_ERR_MSG);
	};

	const style = {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		bgcolor: 'background.paper',
		border: '1px solid gray',
		borderRadius: '10px',
		boxShadow: 24,
		p: 4,
		width: '90vw',
		height: '90vh',
		overflow: 'scroll'
	};

	let shortModalClass = 'modal short-modal';
	if (shortPreviewVisible) {
		shortModalClass += ' show d-block';
	}

	let roModalClass = 'modal ro-modal';
	if (roPreviewVisible) {
		roModalClass += ' show d-block';
	}

	let innerPasteIcon = document?.getElementById('inner-form-paste-icon');
	if (reduxFormCopy?.length > 0 && innerPasteIcon) {
		innerPasteIcon.style.display = 'block';
	} else if (!reduxFormCopy?.length && innerPasteIcon) {
		let innerPasteIcon = document?.getElementById('inner-form-paste-icon');
		innerPasteIcon.style.display = 'none';
	}

	return (
		<>
			<div
				style={{
					margin: '-15px 0px 10px 0px ',
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between'
				}}
			>
				<LastEdited
					data={{
						...formMetaInfo?.updated_by_user,
						time: formMetaInfo?.last_updated_on
					}}
				/>

				<div
					style={{
						width: 'fit-content',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '15px'
					}}
				>
					{reduxFormCopy?.length > 0 && hasEditPermissions && (
						<IconButton
							id="formBuilder-paste-button"
							style={{ visibility: 'hidden' }}
							onClick={handlePasteClick}
							aria-label="paste"
						>
							<PasteIcon />
						</IconButton>
					)}

					{isReadOnly && hasEditPermissions && !isTemplate && (
						<>
							<IconButton onClick={handleCopyFormMenuClick}>
								<MoreHoriz />
							</IconButton>

							<Menu
								anchorEl={anchorEl}
								open={isCopyFormMenuOpen}
								onClose={handleCopyFormMenuClose}
								slotProps={{
									paper: {
										style: {
											width: '20ch'
										}
									}
								}}
							>
								<MenuItem
									disabled={formId == 'undefined' || isTemplatizing}
									onClick={handleTemplatizeForm}
								>
									Copy to template
								</MenuItem>
							</Menu>
						</>
					)}

					{isReadOnly && hasEditPermissions ? (
						<Button
							data-testid={'formBuilder-editButton'}
							variant="outlined"
							onClick={handleEdit}
							startIcon={<EditIcon />}
						>
							Edit
						</Button>
					) : (
						hasEditPermissions && (
							<>
								<LoadingButton
									data-testid={'formBuilder-saveForm'}
									loading={isLoading}
									variant="contained"
									onClick={handleSaveClick}
								>
									Save
								</LoadingButton>
								<Button variant="outlined" onClick={() => dispatch(setFormReadOnly(true))}>
									Cancel
								</Button>
							</>
						)
					)}

					{!hidePreviewButton && PreviewButton}

					<CopyFormModal
						isOpen={isOpen}
						handleClose={handleModalClose}
						formType={formType}
						templateLink={templateLink}
						templateLinkText={templateLinkText}
					/>
					{isReadyToPublish && (
						<WarningModal
							title={warningModalTitle}
							content={warningModalContent}
							listItems={FORM_EDIT_WARNING_LIST}
							confirmationMessage={FORM_EDIT_WARNING_CONFIRMATION_MESSAGE}
							open={warningModal}
							handleClose={handleCloseWarningModal}
							handleConfirm={handleConfirmWarning}
						/>
					)}
					{previewVisible && (
						<Modal
							open={previewVisible}
							aria-labelledby="modal-modal-title"
							aria-describedby="modal-modal-description"
						>
							<Box sx={style}>
								<ReactFormGenerator answer_data={{}} variables={variables} data={dataState} />

								<div className="modal-footer">
									<button
										type="button"
										className="btn btn-default"
										data-dismiss="modal"
										onClick={() => {
											setPreviewVisible(false);
										}}
									>
										Close
									</button>
								</div>
							</Box>
						</Modal>
					)}

					{roPreviewVisible && (
						<div className={roModalClass}>
							<div className="modal-dialog modal-lg">
								<div className="modal-content">
									<ReactFormGenerator
										answer_data={{}}
										read_only={true}
										variables={variables}
										data={dataState}
									/>

									<div className="modal-footer">
										<button
											type="button"
											className="btn btn-default"
											data-dismiss="modal"
											onClick={closePreview.bind(this)}
										>
											Close
										</button>
									</div>
								</div>
							</div>
						</div>
					)}

					{shortPreviewVisible && (
						<div className={shortModalClass}>
							<div className="modal-dialog modal-lg">
								<div className="modal-content">
									<ReactFormGenerator
										answer_data={{}}
										data={dataState}
										display_short={true}
										variables={variables}
									/>

									<div className="modal-footer">
										<button
											type="button"
											className="btn btn-default"
											data-dismiss="modal"
											onClick={closePreview.bind(this)}
										>
											Close
										</button>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<Collapse in={created}>
				<Alert
					severity={'success'}
					action={
						<IconButton
							aria-label="close"
							color={'success'}
							size="small"
							onClick={() => {
								setAlertBalloon(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2 }}
				>
					{formMetaInfo.form_type &&
						`${capitalizeFirstLetter(formMetaInfo.form_type)} form  ${
							formMetaInfo?.is_template == true ? 'template' : ''
						} was successfully created.`}{' '}
					{/* {`The ${formMetaInfo.form_type} form ${
						formMetaInfo.form_type == 'application' ? 'afm' : 'efm'
					}-${formMetaInfo.unique_identifier} was successfully created`} */}
				</Alert>
			</Collapse>

			<Collapse in={alertBalloon.isOpen}>
				<Alert
					severity={alertBalloon.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon.status}
							size="small"
							onClick={() => {
								setAlertBalloon((prevAlertBalloon) => ({
									...prevAlertBalloon,
									isOpen: false
								}));
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2 }}
				>
					{alertBalloon.status == 'error' && errorList.length === 0 && alertBalloon?.message}
					{alertBalloon.status == 'error' && errorList.length > 0 && (
						<>
							<AlertTitle>{alertBalloon?.message}</AlertTitle>
							<ul>
								{errorList.map((error, idx) => (
									<li key={`li-err-${idx}`}>{error}</li>
								))}
							</ul>
						</>
					)}
					{alertBalloon.status !== 'error' &&
						formMetaInfo.form_type &&
						`${capitalizeFirstLetter(formMetaInfo.form_type)} form ${
							formMetaInfo?.is_template == true ? 'template' : ''
						} was successfully saved.`}
				</Alert>
			</Collapse>
		</>
	);
};

export default FormBuilderToolbar;
